// ── Demo sandbox mode ───────────────────────────────────────────────────────
// A public, zero-risk way to explore the app: when demo mode is on, the API
// client swaps its network layer for an in-browser mock (see demoAdapter.ts) —
// the whole dataset lives in this tab's RAM, every change stays in the tab,
// and NOT ONE request leaves the browser. Closing the tab resets everything.
//
// Scope is per-tab (sessionStorage), so a demo tab can run beside a real
// logged-in tab without touching it. Enter via ?demo=admin|employee|client.

export type DemoRole = 'admin' | 'employee' | 'client';

const KEY = 'demoRole';

// Same-origin iframes SHARE the tab's sessionStorage — an embedded demo frame
// (dev-dash previews) must never flip the parent tab into demo mode. Frames
// therefore keep their role in module memory, seeded from their own URL; only
// a top-level window persists to sessionStorage.
const inFrame = (() => { try { return window.self !== window.top; } catch { return true; } })();
let frameRole: DemoRole | null = null;

export function initDemoFromUrl(): void {
  try {
    const q = new URLSearchParams(window.location.search).get('demo');
    if (q && ['admin', 'employee', 'client'].includes(q)) {
      if (inFrame) frameRole = q as DemoRole;
      else sessionStorage.setItem(KEY, q);
    }
  } catch { /* sessionStorage unavailable — no demo */ }
}
initDemoFromUrl(); // eager: frames need the role before any React effect runs

export function demoRole(): DemoRole | null {
  if (inFrame) return frameRole;
  try {
    const r = sessionStorage.getItem(KEY);
    return r === 'admin' || r === 'employee' || r === 'client' ? r : null;
  } catch { return null; }
}

export const isDemo = (): boolean => demoRole() !== null;

export function exitDemo(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}

// The fake identities the sandbox logs you in as (public information)
export function demoUser(role: DemoRole) {
  if (role === 'employee') {
    return { id: 9202, username: 'Demo Carpenter', email: 'demo.worker@ochrepoint.ca', role: 'employee' as const };
  }
  if (role === 'client') {
    return { id: 9301, username: 'Demo Homeowner', email: 'demo.client@ochrepoint.ca', role: 'client' as const };
  }
  return { id: 9001, username: 'Demo Owner', email: 'demo@ochrepoint.ca', role: 'owner' as const };
}
